import "../../styles/blocks/products-slider/products-slider.scss";
import "../../styles/swiper.min.css";

import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { GatsbyImage as Image } from "gatsby-plugin-image";
import SwiperCore, { Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import IconChevronLeft from "../../icons/IconChevronLeft";

SwiperCore.use([Autoplay]);

const getProductTypeLabel = (type) => {
	if (type === "app") {
		return "App";
	}

	if (type === "project") {
		return "Project";
	}

	return "Theme";
};

const ProductLink = ({ link, variant, productTitle, productType }) => {
	if (!link?.url) {
		return null;
	}

	const target = link.target || undefined;
	const rel = target === "_blank" ? "noreferrer" : undefined;
	const linkText = link.title || "View product";
	const isGenericReadMore = linkText.trim().toLowerCase() === "read more";

	return (
		<a
			className={`products-slider__card-button button button--products-landing button--${variant}`}
			href={link.url}
			target={target}
			rel={rel}
		>
			{linkText}
			{isGenericReadMore && (
				<span className="visually-hidden"> about the {productTitle} {productType.toLowerCase()}</span>
			)}
		</a>
	);
};

const ProductCard = ({ item }) => {
	const gatsbyImageData = item.image?.localFile?.childImageSharp?.gatsbyImageData;
	const title = item.title || "Product";
	const productType = getProductTypeLabel(item.type);

	return (
		<article className="products-slider__card">
			<div className="products-slider__card-image-wrapper">
				{gatsbyImageData ? (
					<Image
						className="products-slider__card-image"
						image={gatsbyImageData}
						alt={item.image?.altText || title}
						title={item.image?.altText || title}
					/>
				) : (
					<div className="products-slider__card-image-placeholder" aria-hidden="true" />
				)}
			</div>

			<div className="products-slider__card-content">
				<div className="products-slider__card-heading-row">
					<p className="products-slider__card-type">
						{productType}
					</p>

					<h3 className="products-slider__card-title">
						{title}
					</h3>
				</div>

				<div className="products-slider__card-links">
					<ProductLink link={item.primaryLink} variant="secondary" productTitle={title} productType={productType} />
					<ProductLink link={item.secondaryLink} variant="primary" productTitle={title} productType={productType} />
				</div>
			</div>
		</article>
	);
};

const ProductsSlider = ({ items, title }) => {
	const swiperRef = useRef(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const sliderItems = Array.isArray(items)
		? items.filter((item) => item?.slug)
		: [];

	if (!sliderItems.length) {
		return null;
	}

	const progress = ((activeIndex + 1) / sliderItems.length) * 100;

	return (
		<section className="products-slider">
			<div className="products-slider__container container container--products-landing">
				<h2 className="products-slider__title">
					{title}
				</h2>

				<Swiper
					className="products-slider__swiper"
					loop={true}
					// Keep loop duplicates identical during SSR and hydration at every breakpoint.
					loopedSlides={sliderItems.length}
					autoplay={{
						delay: 3000,
						disableOnInteraction: false,
					}}
					breakpoints={{
						0: {
							slidesPerView: 1,
							spaceBetween: 16,
						},
						767: {
							slidesPerView: 2,
							spaceBetween: 16,
						},
						1024: {
							slidesPerView: 3,
							spaceBetween: 16,
						},
						1440: {
							slidesPerView: 4,
							spaceBetween: 16,
						},
					}}
					onSwiper={(swiper) => {
						swiperRef.current = swiper;
					}}
					onSlideChange={(swiper) => {
						setActiveIndex(swiper.realIndex || 0);
					}}
				>
					{sliderItems.map((item) => (
						<SwiperSlide
							className="products-slider__slide"
							key={`${item.type}-${item.slug}`}
						>
							<ProductCard item={item} />
						</SwiperSlide>
					))}
				</Swiper>

				<div className="products-slider__controls">
					<div className="products-slider__progress" aria-hidden="true">
						<div
							className="products-slider__progress-bar"
							style={{ width: `${progress}%` }}
						/>
					</div>

					<div className="products-slider__buttons">
						<button
							className="products-slider__button products-slider__button--prev"
							type="button"
							aria-label="Show previous product"
							onClick={() => swiperRef.current?.slidePrev()}
						>
							<IconChevronLeft />
						</button>

						<button
							className="products-slider__button products-slider__button--next"
							type="button"
							aria-label="Show next product"
							onClick={() => swiperRef.current?.slideNext()}
						>
							<IconChevronLeft />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

const linkShape = PropTypes.shape({
	title: PropTypes.string,
	url: PropTypes.string,
	target: PropTypes.string,
});

const imageShape = PropTypes.shape({
	altText: PropTypes.string,
	localFile: PropTypes.shape({
		childImageSharp: PropTypes.shape({
			gatsbyImageData: PropTypes.object,
		}),
	}),
});

ProductLink.propTypes = {
	link: linkShape,
	variant: PropTypes.oneOf(["primary", "secondary"]).isRequired,
	productTitle: PropTypes.string.isRequired,
	productType: PropTypes.string.isRequired,
};

ProductCard.propTypes = {
	item: PropTypes.shape({
		type: PropTypes.string,
		title: PropTypes.string,
		image: imageShape,
		primaryLink: linkShape,
		secondaryLink: linkShape,
	}).isRequired,
};

ProductsSlider.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			type: PropTypes.string,
			title: PropTypes.string,
			image: imageShape,
			primaryLink: linkShape,
			secondaryLink: linkShape,
			slug: PropTypes.string,
			url: PropTypes.string,
		})
	),
	title: PropTypes.string,
};

ProductsSlider.defaultProps = {
	items: [],
	title: "Our Products",
};

export default ProductsSlider;
